'use client';
import React, { useState } from 'react';
import Image from 'next/image';
import Button from '@/components/ui/Button';
import ProductCarousel from '@/components/ui/ProductCarousel';
import { ProductDetail } from '@/types/product';
import useStrapiHome from '@/hooks/useStrapiHome';
import useLoMasVendidoProducts from '@/hooks/useLoMasVendidoProducts';
import StrapiBlocksRenderer from '@/components/ui/StrapiBlocksRenderer';
import { BreadcrumbMenu } from '@/types/menu';

interface ProductsSectionProps {
  className?: string;
  title?: string;
  subtitle?: string;
  products?: ProductDetail[];
}

const ProductsSection: React.FC<ProductsSectionProps> = ({ 
  className = '',
  title = 'LO MÁS VENDIDO',
  subtitle = 'Seguro que te interesa',
  products = [],
}) => {
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const { data: strapiData, loading: strapiLoading } = useStrapiHome();
  const { data: loMasVendidoProducts, isLoading: productsLoading, error: productsError } = useLoMasVendidoProducts();

  // Verificar si la sección debe ser visible
  const isVisible = strapiData?.loMasVendido?.visible ?? true;

  // No renderizar si no es visible
  if (!isVisible) {
    return null;
  }

  // Usar productos de Strapi/PrestaShop si están disponibles, sino usar los proporcionados
  const productsToUse = loMasVendidoProducts && loMasVendidoProducts.length > 0 
    ? loMasVendidoProducts 
    : products;


  // Extraer título y subtítulo de Strapi si están disponibles
  const strapiTitle = strapiData?.loMasVendido?.title;

  // Usar título de Strapi si está disponible, sino usar el título por defecto
  const finalTitle = strapiTitle && strapiTitle.length > 0 
    ? <StrapiBlocksRenderer data-component-id="src/components/sections/ProductsSection.tsx:48:6" data-component-path="src/components/sections/ProductsSection.tsx" data-component-line="48" data-component-file="ProductsSection.tsx" data-component-name="StrapiBlocksRenderer" data-component-content="%7B%22elementName%22%3A%22StrapiBlocksRenderer%22%7D" blocks={strapiTitle} /> 
    : title;


  const handleFavoriteToggle = (id: string) => {
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(id)) {
        newFavorites.delete(id);
      } else {
        newFavorites.add(id);
      }
      return newFavorites;
    });
  };

  // Mostrar loading solo para productos, pero mostrar título inmediatamente
  const showLoadingSpinner = productsLoading;

  const breadcrumbMenu: BreadcrumbMenu = {
    menuLevel1: {
      id: 0,
      description: '',
      level: 0,
      parent: 0,
      name: 'Lo más vendido',
      slug: 'lo-mas-vendido',
      pageNumbers: 0,
      url: '',
    },
  };

  return (
    <div data-component-id="src/components/sections/ProductsSection.tsx:81:4" data-component-path="src/components/sections/ProductsSection.tsx" data-component-line="81" data-component-file="ProductsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`${className}`}>
      <div data-component-id="src/components/sections/ProductsSection.tsx:82:6" data-component-path="src/components/sections/ProductsSection.tsx" data-component-line="82" data-component-file="ProductsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-8%20text-left%22%7D" className="mb-8 text-left">
        <h2 data-component-id="src/components/sections/ProductsSection.tsx:83:8" data-component-path="src/components/sections/ProductsSection.tsx" data-component-line="83" data-component-file="ProductsSection.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22font-tt-norms-extra-black%20text-%5B40px%5D%20leading-%5B48px%5D%20text-global-1%20mb-2%22%7D" className="font-tt-norms-extra-black text-[40px] leading-[48px] text-global-1 mb-2">
          {finalTitle}
        </h2>
        <div data-component-id="src/components/sections/ProductsSection.tsx:86:8" data-component-path="src/components/sections/ProductsSection.tsx" data-component-line="86" data-component-file="ProductsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-global-9%20rounded%20px-4%20inline-block%22%7D" className="bg-global-9 rounded px-4 inline-block">
          <span data-component-id="src/components/sections/ProductsSection.tsx:87:10" data-component-path="src/components/sections/ProductsSection.tsx" data-component-line="87" data-component-file="ProductsSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-tt-norms-medium%20text-%5B22px%5D%20leading-%5B27px%5D%20text-global-1%22%7D" className="font-tt-norms-medium text-[22px] leading-[27px] text-global-1">
            {subtitle}
          </span>
        </div>
      </div>
      
      {showLoadingSpinner ? (
        <div data-component-id="src/components/sections/ProductsSection.tsx:94:8" data-component-path="src/components/sections/ProductsSection.tsx" data-component-line="94" data-component-file="ProductsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20justify-center%20items-center%20h-64%22%7D" className="flex justify-center items-center h-64">
          <div data-component-id="src/components/sections/ProductsSection.tsx:95:10" data-component-path="src/components/sections/ProductsSection.tsx" data-component-line="95" data-component-file="ProductsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-spin%20rounded-full%20h-8%20w-8%20border-b-2%20border-orange-500%22%7D" className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
          <span data-component-id="src/components/sections/ProductsSection.tsx:96:10" data-component-path="src/components/sections/ProductsSection.tsx" data-component-line="96" data-component-file="ProductsSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22ml-2%20text-gray-600%22%2C%22textContent%22%3A%22Cargando%20productos...%22%7D" className="ml-2 text-gray-600">Cargando productos...</span>
        </div>
      ) : (
        <ProductCarousel data-component-id="src/components/sections/ProductsSection.tsx:99:8" data-component-path="src/components/sections/ProductsSection.tsx" data-component-line="99" data-component-file="ProductsSection.tsx" data-component-name="ProductCarousel" data-component-content="%7B%22elementName%22%3A%22ProductCarousel%22%2C%22className%22%3A%22mb-8%22%7D"
          breadcrumbMenu={breadcrumbMenu}
          items={productsToUse}
          className="mb-8"
          onFavoriteToggle={handleFavoriteToggle}
          defaultVisibleCount={5}
          showNavigation={true}
        />
      )}
    </div>
  );
};

export default ProductsSection; 